import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckSquare,
  ChevronRight,
  ClipboardList,
  Link2,
  Loader2,
  Plus,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../../services/api";

const FREQUENCIES = [
  { value: "monthly", label: "Monthly", hint: "Every pay month" },
  { value: "one_time", label: "One-time", hint: "Single specific month" },
  { value: "selected_months", label: "Selected months", hint: "Pick calendar months" },
  { value: "quarterly", label: "Quarterly", hint: "Default Mar / Jun / Sep / Dec" },
  { value: "half_yearly", label: "Half-yearly", hint: "Default Apr / Oct" },
  { value: "yearly", label: "Yearly", hint: "Once a year" },
  { value: "date_range", label: "Date range", hint: "Inclusive from–to months" },
];

const MONTH_OPTS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2020, i, 1).toLocaleString("en", { month: "short" }),
}));

function emptyMeta(freq) {
  if (freq === "one_time") return { oneTimeMonth: "" };
  if (freq === "selected_months") return { months: [] };
  if (freq === "quarterly") return { months: [3, 6, 9, 12] };
  if (freq === "half_yearly") return { months: [4, 10] };
  if (freq === "yearly") return { month: 3 };
  if (freq === "date_range") return { fromMonth: "", toMonth: "" };
  return {};
}

function newItem(label = "") {
  return {
    key: `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    label,
    required: true,
    linkType: "",
    linkPath: "",
    linkLabel: "",
  };
}

function blankDraft() {
  return {
    id: null,
    name: "",
    description: "",
    frequency: "monthly",
    specificMonthsJson: {},
    itemsJson: [newItem()],
    active: true,
    sortOrder: 0,
  };
}

function schedulePreview(frequency, meta = {}) {
  const freq = frequency || "monthly";
  if (freq === "monthly" || freq === "once_per_month" || freq === "recurring") {
    return "Due every pay month";
  }
  if (freq === "one_time") {
    return meta.oneTimeMonth ? `Due only in ${meta.oneTimeMonth}` : "Pick a month";
  }
  if (["selected_months", "quarterly", "half_yearly"].includes(freq)) {
    const months = Array.isArray(meta.months) ? meta.months.map(Number) : [];
    if (!months.length) return "No months selected";
    return `Due in ${months
      .map((m) => MONTH_OPTS.find((o) => o.value === m)?.label || m)
      .join(", ")}`;
  }
  if (freq === "yearly") {
    const m = Number(meta.month || 3);
    return `Due every ${MONTH_OPTS.find((o) => o.value === m)?.label || m}`;
  }
  if (freq === "date_range") {
    if (!meta.fromMonth && !meta.toMonth) return "Set from / to months";
    return `${meta.fromMonth || "…"} → ${meta.toMonth || "…"}`;
  }
  return freq;
}

function freqLabel(value) {
  return FREQUENCIES.find((f) => f.value === value)?.label || value || "Monthly";
}

export default function PayrollChecklistEditor({ darkMode = false, canEdit = true }) {
  const [checklists, setChecklists] = useState([]);
  const [linkCatalog, setLinkCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | inactive
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";
  const card = darkMode ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))]" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))] ${
    darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-white" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
  }`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [clRes, linkRes] = await Promise.all([
        payrollOpsService.listChecklists(),
        payrollOpsService.checklistLinkCatalog().catch(() => ({ data: [] })),
      ]);
      const rows = Array.isArray(clRes?.data) ? clRes.data : [];
      setChecklists(rows);
      setLinkCatalog(Array.isArray(linkRes?.data) ? linkRes.data : []);
      setSelectedId((prev) => {
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return rows[0]?.id ?? null;
      });
    } catch (err) {
      setChecklists([]);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load checklists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedId) {
      setDraft(null);
      setDirty(false);
      return;
    }
    const row = checklists.find((c) => c.id === selectedId);
    if (!row) return;
    setDraft({
      id: row.id,
      name: row.name || "",
      description: row.description || "",
      frequency: row.frequency || "monthly",
      specificMonthsJson: row.specificMonthsJson || {},
      itemsJson: Array.isArray(row.itemsJson) && row.itemsJson.length ? row.itemsJson : [newItem()],
      active: row.active !== false,
      sortOrder: Number(row.sortOrder) || 0,
    });
    setDirty(false);
    setConfirmDelete(false);
  }, [selectedId, checklists]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return checklists.filter((c) => {
      if (filter === "active" && c.active === false) return false;
      if (filter === "inactive" && c.active !== false) return false;
      if (!q) return true;
      const hay = `${c.name || ""} ${c.description || ""} ${(c.itemsJson || [])
        .map((i) => i.label)
        .join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [checklists, search, filter]);

  const patchDraft = (patch) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
  };

  const startCreate = () => {
    if (!canEdit) return;
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    const d = blankDraft();
    setSelectedId(null);
    setDraft(d);
    setDirty(true);
    setConfirmDelete(false);
  };

  const handleSave = async () => {
    if (!canEdit || !draft) return;
    if (!draft.name.trim()) {
      toast.error("Checklist name is required");
      return;
    }
    const items = (draft.itemsJson || [])
      .map((it) => ({
        ...it,
        label: String(it.label || "").trim(),
        key: it.key || newItem().key,
      }))
      .filter((it) => it.label);
    if (!items.length) {
      toast.error("Add at least one task");
      return;
    }
    const freq = draft.frequency || "monthly";
    const meta = draft.specificMonthsJson || {};
    if (freq === "one_time" && !meta.oneTimeMonth) {
      toast.error("Select the one-time month");
      return;
    }
    if (freq === "selected_months" && !(meta.months || []).length) {
      toast.error("Select at least one month");
      return;
    }
    if (freq === "date_range" && meta.fromMonth && meta.toMonth && meta.fromMonth > meta.toMonth) {
      toast.error("From month must be before To month");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description?.trim() || null,
        frequency: freq,
        specificMonthsJson: meta,
        itemsJson: items,
        active: draft.active !== false,
        sortOrder: Number(draft.sortOrder) || 0,
      };
      let res;
      if (draft.id) {
        res = await payrollOpsService.updateChecklist(draft.id, payload);
        toast.success("Checklist saved");
      } else {
        res = await payrollOpsService.createChecklist(payload);
        toast.success("Checklist created");
      }
      const savedId = res?.data?.id;
      await load();
      if (savedId) setSelectedId(savedId);
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canEdit || !draft?.id) return;
    setSaving(true);
    try {
      await payrollOpsService.deleteChecklist(draft.id);
      toast.success("Checklist deleted");
      setConfirmDelete(false);
      setDirty(false);
      setSelectedId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (idx, patch) => {
    const items = [...(draft.itemsJson || [])];
    items[idx] = { ...items[idx], ...patch };
    patchDraft({ itemsJson: items });
  };

  const removeItem = (idx) => {
    const items = (draft.itemsJson || []).filter((_, i) => i !== idx);
    patchDraft({ itemsJson: items.length ? items : [newItem()] });
  };

  const moveItem = (idx, dir) => {
    const items = [...(draft.itemsJson || [])];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    patchDraft({ itemsJson: items });
  };

  const applyLinkPreset = (idx, linkType) => {
    const preset = linkCatalog.find((l) => l.linkType === linkType);
    updateItem(idx, {
      linkType: linkType || "",
      linkPath: preset?.linkPath || "",
      linkLabel: preset?.linkLabel || "",
    });
  };

  const renderScheduleFields = () => {
    if (!draft) return null;
    const freq = draft.frequency || "monthly";
    const meta = draft.specificMonthsJson || {};
    const setMeta = (next) => patchDraft({ specificMonthsJson: next });

    if (freq === "one_time") {
      return (
        <label className="block text-xs">
          <span className={muted}>Pay month</span>
          <input
            type="month"
            className={`${inputCls} mt-1`}
            value={meta.oneTimeMonth || ""}
            disabled={!canEdit}
            onChange={(e) => setMeta({ ...meta, oneTimeMonth: e.target.value })}
          />
        </label>
      );
    }

    if (["selected_months", "quarterly", "half_yearly"].includes(freq)) {
      const months = Array.isArray(meta.months) ? meta.months.map(Number) : [];
      return (
        <div>
          <p className={`mb-2 text-xs ${muted}`}>Select months</p>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
            {MONTH_OPTS.map((m) => {
              const on = months.includes(m.value);
              return (
                <button
                  key={m.value}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => {
                    const next = on
                      ? months.filter((x) => x !== m.value)
                      : [...months, m.value].sort((a, b) => a - b);
                    setMeta({ ...meta, months: next });
                  }}
                  className={`rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${
                    on
                      ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))]/10 text-[hsl(var(--ds-primary))]"
                      : darkMode
                      ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                      : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (freq === "yearly") {
      return (
        <label className="block text-xs">
          <span className={muted}>Month each year</span>
          <select
            className={`${inputCls} mt-1`}
            value={Number(meta.month || 3)}
            disabled={!canEdit}
            onChange={(e) => setMeta({ ...meta, month: Number(e.target.value) })}
          >
            {MONTH_OPTS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    if (freq === "date_range") {
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs">
            <span className={muted}>From</span>
            <input
              type="month"
              className={`${inputCls} mt-1`}
              value={meta.fromMonth || ""}
              disabled={!canEdit}
              onChange={(e) => setMeta({ ...meta, fromMonth: e.target.value })}
            />
          </label>
          <label className="block text-xs">
            <span className={muted}>To</span>
            <input
              type="month"
              className={`${inputCls} mt-1`}
              value={meta.toMonth || ""}
              disabled={!canEdit}
              onChange={(e) => setMeta({ ...meta, toMonth: e.target.value })}
            />
          </label>
        </div>
      );
    }

    return (
      <p className={`text-xs ${muted}`}>Appears automatically on every Run Payroll month.</p>
    );
  };

  if (loading) {
    return (
      <div className={`flex h-48 items-center justify-center gap-2 rounded-xl border ${card}`}>
        <Loader2 className="animate-spin text-[hsl(var(--ds-primary))]" size={18} />
        <span className={`text-sm ${muted}`}>Loading checklist tasks…</span>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[520px] flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-[hsl(var(--ds-fg))]"}`}>
            Checklist Tasks
          </h2>
          <p className={`mt-0.5 text-xs ${muted}`}>
            Configure tasks that appear on Run Payroll → Checklist for matching pay months.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={startCreate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 py-2 text-xs font-semibold text-white shadow-sm hover:opacity-95"
          >
            <Plus size={14} /> New checklist
          </button>
        )}
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[300px_1fr]">
        {/* List */}
        <aside className={`flex min-h-0 flex-col overflow-hidden rounded-xl border shadow-sm ${card}`}>
          <div className={`space-y-2 border-b p-3 ${border}`}>
            <div className="relative">
              <Search size={14} className={`absolute left-2.5 top-2.5 ${muted}`} />
              <input
                className={`${inputCls} pl-8`}
                placeholder="Search checklists…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-1">
              {[
                ["all", "All"],
                ["active", "Active"],
                ["inactive", "Inactive"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFilter(id)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                    filter === id
                      ? "bg-[hsl(var(--ds-primary))] text-white"
                      : darkMode
                      ? "bg-[#333] text-[hsl(var(--ds-muted-fg))]"
                      : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {filtered.map((c) => {
              const selected = selectedId === c.id && !!draft?.id;
              const count = (c.itemsJson || []).length;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    if (dirty && !window.confirm("Discard unsaved changes?")) return;
                    setSelectedId(c.id);
                  }}
                  className={`mb-1 flex w-full items-start gap-2 rounded-lg px-3 py-2.5 text-left transition-colors ${
                    selected
                      ? darkMode
                        ? "bg-[hsl(var(--ds-primary))]/20 ring-1 ring-[hsl(var(--ds-primary))]/40"
                        : "bg-red-50 ring-1 ring-[hsl(var(--ds-primary))]/30"
                      : darkMode
                      ? "hover:bg-[hsl(var(--ds-muted))]"
                      : "hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  <ClipboardList
                    size={16}
                    className={`mt-0.5 shrink-0 ${c.active === false ? muted : "text-[hsl(var(--ds-primary))]"}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-semibold">{c.name}</span>
                      {c.active === false && (
                        <span className="rounded bg-[hsl(var(--ds-muted))] px-1.5 py-0.5 text-[9px] uppercase text-[hsl(var(--ds-muted-fg))]">
                          Off
                        </span>
                      )}
                    </div>
                    <p className={`mt-0.5 truncate text-[11px] ${muted}`}>
                      {freqLabel(c.frequency)} · {count} task{count === 1 ? "" : "s"}
                    </p>
                    <p className={`truncate text-[10px] ${muted}`}>
                      {schedulePreview(c.frequency, c.specificMonthsJson)}
                    </p>
                  </div>
                  <ChevronRight size={14} className={`mt-1 shrink-0 ${muted}`} />
                </button>
              );
            })}
            {!filtered.length && (
              <div className={`px-3 py-8 text-center text-xs ${muted}`}>
                No checklists match your filters.
              </div>
            )}
          </div>
        </aside>

        {/* Editor */}
        <section className={`flex min-h-0 flex-col overflow-hidden rounded-xl border shadow-sm ${card}`}>
          {!draft ? (
            <div className={`flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center`}>
              <CheckSquare size={36} className={muted} />
              <div>
                <p className="text-sm font-semibold">No checklist selected</p>
                <p className={`mt-1 text-xs ${muted}`}>
                  Choose one from the list, or create a new checklist task set.
                </p>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={startCreate}
                  className="rounded-lg bg-[hsl(var(--ds-primary))] px-4 py-2 text-xs font-semibold text-white"
                >
                  Create checklist
                </button>
              )}
            </div>
          ) : (
            <>
              <div
                className={`flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 ${border}`}
              >
                <div>
                  <p className="text-sm font-semibold">
                    {draft.id ? "Edit checklist" : "New checklist"}
                  </p>
                  <p className={`text-[11px] ${muted}`}>
                    {schedulePreview(draft.frequency, draft.specificMonthsJson)}
                    {dirty ? " · Unsaved changes" : ""}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canEdit && draft.id && (
                    confirmDelete ? (
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="text-red-600">Delete?</span>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={handleDelete}
                          className="rounded bg-[hsl(var(--ds-danger))] px-2 py-1 text-white"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(false)}
                          className="rounded border px-2 py-1"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(true)}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs text-red-600"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    )
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      disabled={saving || !dirty}
                      onClick={handleSave}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-45"
                    >
                      {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      Save
                    </button>
                  )}
                </div>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-xs sm:col-span-2">
                    <span className={`font-medium ${muted}`}>Name</span>
                    <input
                      className={`${inputCls} mt-1`}
                      value={draft.name}
                      disabled={!canEdit}
                      placeholder="e.g. Monthly compliance checks"
                      onChange={(e) => patchDraft({ name: e.target.value })}
                    />
                  </label>
                  <label className="block text-xs sm:col-span-2">
                    <span className={`font-medium ${muted}`}>Description</span>
                    <textarea
                      rows={2}
                      className={`${inputCls} mt-1 resize-none`}
                      value={draft.description}
                      disabled={!canEdit}
                      placeholder="Optional notes for payroll operators"
                      onChange={(e) => patchDraft({ description: e.target.value })}
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={draft.active !== false}
                      disabled={!canEdit}
                      onChange={(e) => patchDraft({ active: e.target.checked })}
                    />
                    <span>Active — appear on Run Payroll when due</span>
                  </label>
                </div>

                <div>
                  <p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>
                    Frequency
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {FREQUENCIES.map((f) => {
                      const on = draft.frequency === f.value;
                      return (
                        <button
                          key={f.value}
                          type="button"
                          disabled={!canEdit}
                          onClick={() =>
                            patchDraft({
                              frequency: f.value,
                              specificMonthsJson: emptyMeta(f.value),
                            })
                          }
                          className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                            on
                              ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))]/5 ring-1 ring-[hsl(var(--ds-primary))]/30"
                              : darkMode
                              ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                              : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          }`}
                        >
                          <div className="text-xs font-semibold">{f.label}</div>
                          <div className={`mt-0.5 text-[10px] ${muted}`}>{f.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className={`mt-3 rounded-lg border p-3 ${border}`}>{renderScheduleFields()}</div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className={`text-xs font-semibold uppercase tracking-wide ${muted}`}>
                      Tasks ({(draft.itemsJson || []).length})
                    </p>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() =>
                          patchDraft({ itemsJson: [...(draft.itemsJson || []), newItem()] })
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--ds-primary))]"
                      >
                        <Plus size={12} /> Add task
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {(draft.itemsJson || []).map((item, idx) => (
                      <div
                        key={item.key || idx}
                        className={`rounded-xl border p-3 ${border} ${
                          darkMode ? "bg-[hsl(var(--ds-surface))]" : "bg-[hsl(var(--ds-muted))]/80"
                        }`}
                      >
                        <div className="flex flex-wrap items-start gap-2">
                          <span className={`mt-2 w-5 text-center text-[11px] font-semibold ${muted}`}>
                            {idx + 1}
                          </span>
                          <div className="min-w-0 flex-1 space-y-2">
                            <input
                              className={inputCls}
                              value={item.label || ""}
                              disabled={!canEdit}
                              placeholder="Task label"
                              onChange={(e) => updateItem(idx, { label: e.target.value })}
                            />
                            <div className="flex flex-wrap items-center gap-3 text-xs">
                              <label className="inline-flex items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  checked={item.required !== false}
                                  disabled={!canEdit}
                                  onChange={(e) => updateItem(idx, { required: e.target.checked })}
                                />
                                Required
                              </label>
                              <label className={`inline-flex items-center gap-1.5 ${muted}`}>
                                <Link2 size={12} />
                                <select
                                  className={`${inputCls} w-auto py-1`}
                                  value={item.linkType || ""}
                                  disabled={!canEdit}
                                  onChange={(e) => applyLinkPreset(idx, e.target.value)}
                                >
                                  <option value="">No module link</option>
                                  {linkCatalog.map((l) => (
                                    <option key={l.linkType} value={l.linkType}>
                                      {l.linkLabel}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>
                            {item.linkType === "external" && (
                              <input
                                className={inputCls}
                                placeholder="https://…"
                                value={item.linkPath || ""}
                                disabled={!canEdit}
                                onChange={(e) => updateItem(idx, { linkPath: e.target.value })}
                              />
                            )}
                          </div>
                          {canEdit && (
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                title="Move up"
                                onClick={() => moveItem(idx, -1)}
                                className={`rounded px-1.5 py-0.5 text-[10px] ${muted}`}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                title="Move down"
                                onClick={() => moveItem(idx, 1)}
                                className={`rounded px-1.5 py-0.5 text-[10px] ${muted}`}
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                title="Remove"
                                onClick={() => removeItem(idx)}
                                className="rounded p-1 text-red-500 hover:bg-red-50"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
